import {LoadingButton} from '@mui/lab'
import type {GridRowId, GridRowModel, GridRowSelectionModel} from '@mui/x-data-grid'
import type {ReactElement} from 'react'
import {useCallback, useEffect, useRef, useState} from 'react'
import {useCustomTranslation} from '@hooks/useCustomTranslation'

interface UseDataGridSelectAllArgs {
  /** Rows of the CURRENT page (what the grid renders). */
  rows: GridRowModel[]
  /** Total row count across all pages (the API count). */
  rowCount: number
  rowSelectionModel?: GridRowSelectionModel
  onRowSelectionModelChange?: (model: GridRowSelectionModel) => void
  /**
   * Resolves every id in the full (filtered) data set. Providing it enables the
   * "Select all (N)" control; it owns its own error reporting.
   */
  fetchAllIds?: () => Promise<GridRowId[]>
  /** Gate for the control, e.g. the table's checkboxSelection flag. */
  enabled?: boolean
}

interface UseDataGridSelectAllResult {
  /** Pass to the DataGrid instead of the raw onRowSelectionModelChange. */
  handleRowSelectionModelChange: (model: GridRowSelectionModel) => void
  /** "Select all (N)" / "Clear selection" button; render near the table's search/filters. */
  selectAllControl: ReactElement | null
}

/**
 * Shared cross-page selection behavior for server-paginated data grids.
 *
 * The grid's native header checkbox emits an exclude model ("everything but ...") which
 * downstream consumers can only resolve against the fetched page. This hook converts it
 * to a concrete include model covering the CURRENT PAGE ONLY (union with what was already
 * selected); selecting every row across pages is the explicit "Select all (N)" control's
 * job, which resolves concrete ids via fetchAllIds.
 */
const useDataGridSelectAll = ({
  rows,
  rowCount,
  rowSelectionModel,
  onRowSelectionModelChange,
  fetchAllIds,
  enabled = true,
}: UseDataGridSelectAllArgs): UseDataGridSelectAllResult => {
  const [tCommon] = useCustomTranslation('common')
  const [isSelectingAll, setIsSelectingAll] = useState<boolean>(false)
  // Some rows may not be selectable (e.g. yourself in the users table), so fetchAllIds can
  // return fewer ids than rowCount. Remember its size: that is the real "all selected"
  // target, otherwise the control would never flip to "Clear selection".
  const [selectableRowCount, setSelectableRowCount] = useState<number | null>(null)
  const previousRowCountRef = useRef(rowCount)
  useEffect(() => {
    if (previousRowCountRef.current !== rowCount) {
      previousRowCountRef.current = rowCount
      setSelectableRowCount(null)
    }
  }, [rowCount])

  const handleRowSelectionModelChange = useCallback(
    (newModel: GridRowSelectionModel): void => {
      if (newModel.type === 'exclude' && rowSelectionModel) {
        const ids = new Set<GridRowId>(
          rowSelectionModel.type === 'include' ? rowSelectionModel.ids : [],
        )
        rows.forEach((row) => ids.add(row.id as GridRowId))
        newModel.ids.forEach((id) => ids.delete(id))
        onRowSelectionModelChange?.({type: 'include', ids})
        return
      }
      onRowSelectionModelChange?.(newModel)
    },
    [rows, rowSelectionModel, onRowSelectionModelChange],
  )

  const selectedCount =
    rowSelectionModel?.type === 'include'
      ? rowSelectionModel.ids.size
      : rowSelectionModel
        ? rowCount - rowSelectionModel.ids.size
        : 0
  const allSelectedTarget = selectableRowCount ?? rowCount
  const allSelected = allSelectedTarget > 0 && selectedCount >= allSelectedTarget

  const handleSelectAllRows = async (): Promise<void> => {
    if (!fetchAllIds) {
      return
    }
    setIsSelectingAll(true)
    try {
      const ids = await fetchAllIds()
      setSelectableRowCount(ids.length)
      onRowSelectionModelChange?.({type: 'include', ids: new Set(ids)})
    } catch {
      // The caller's fetchAllIds owns error reporting; keep the current selection.
    } finally {
      setIsSelectingAll(false)
    }
  }

  const handleClearSelection = (): void => {
    onRowSelectionModelChange?.({type: 'include', ids: new Set([])})
  }

  const selectAllControl =
    fetchAllIds && enabled && selectedCount > 0 ? (
      <LoadingButton
        data-cy="mbai-basic-data-grid-select-all-button"
        variant="outlined"
        size="small"
        loading={isSelectingAll}
        onClick={allSelected ? handleClearSelection : handleSelectAllRows}
        sx={{whiteSpace: 'nowrap', flexShrink: 0}}
      >
        {allSelected
          ? tCommon('data_grid.clear_selection')
          : tCommon('data_grid.select_all_rows', {count: rowCount})}
      </LoadingButton>
    ) : null

  return {handleRowSelectionModelChange, selectAllControl}
}

export default useDataGridSelectAll
